import { Button, IconHeart, Text } from '@consumidor-positivo/aurora';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { useRedline } from '@libs/redline';
import { useEffect, useRef, useState } from 'react';

import './styles.scss';

export const SURVEY_EVENTS = {
  agreement_detail_one: 'agreement_detail_one',
  agreement_detail_two: 'agreement_detail_two',
  abandoned_cart: 'abandoned_cart',
  payment_view: 'payment_view',
} as const;

export const CONFIRMATION_VISITS_KEY = 'hotjar_confirmation_visits';
export const SURVEY_STORAGE_KEY = 'ac-ces';

const CES_COOKIE = 'dx-ces';

type SurveyName = keyof typeof SURVEY_EVENTS;
type ClickableSurveyName = Exclude<SurveyName, 'abandoned_cart'>;
type SurveyClickedState = Partial<Record<ClickableSurveyName, boolean>>;

const getSurveyState = (): SurveyClickedState => {
  try {
    return JSON.parse(sessionStorage.getItem(SURVEY_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

export interface HotjarSurveyProps {
  surveyName: SurveyName;
}

export const fireHotjarEvent = (eventName: string): void => {
  (window as Window & { hj?: (...args: unknown[]) => void }).hj?.('event', eventName);
};

const SURVEY_DETECTION_TIMEOUT_MS = 10_000;

const findHotjarSurveyElement = (): Element | null =>
  document.querySelector('[data-testid="survey-root"]') ||
  document.querySelector('[id^="hotjar-survey-"]') ||
  document.querySelector('[aria-labelledby^="hj-survey-lbl"]');

const HotjarSurvey = ({ surveyName }: HotjarSurveyProps) => {
  const { track } = useRedline();
  const [hasClicked, setHasClicked] = useState(() => !!getSurveyState()[surveyName as ClickableSurveyName]);
  const [isWaiting, setIsWaiting] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const cesCookieValue = getCookie(CES_COOKIE);
  const isControl = cesCookieValue?.includes('control');
  const isCes = cesCookieValue?.includes('ces');

  if (isControl || !isCes || hasClicked) return null;

  const commitSurveyOpened = () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const updated = { ...getSurveyState(), [surveyName]: true };
    sessionStorage.setItem(SURVEY_STORAGE_KEY, JSON.stringify(updated));
    setHasClicked(true);
  };

  const handleOpen = () => {
    if (isWaiting) return;
    setIsWaiting(true);

    fireHotjarEvent(SURVEY_EVENTS[surveyName]);

    track.userTracking.elementClicked({
      location: window.location.pathname,
      name: `hotjar-survey-${surveyName}`,
      text: 'Quero avaliar',
    });

    if (findHotjarSurveyElement()) {
      commitSurveyOpened();
      return;
    }

    observerRef.current = new MutationObserver(() => {
      if (findHotjarSurveyElement()) commitSurveyOpened();
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    timeoutRef.current = setTimeout(commitSurveyOpened, SURVEY_DETECTION_TIMEOUT_MS);
  };

  return (
    <div className="ac-hotjar-survey">
      <div className="ac-hotjar-survey__header">
        <div className="ac-hotjar-survey__icon-wrapper">
          <div className="ac-hotjar-survey__icon-container">
            <IconHeart />
          </div>
        </div>
      </div>

      <div className="ac-hotjar-survey__body">
        <div className="ac-hotjar-survey__text">
          <Text as="h3" weight="bold" variant="body-medium" className="ac-hotjar-survey__title">
            Avalie sua negociação até aqui
          </Text>
          <Text as="p" variant="body-medium" color="secondary" className="ac-hotjar-survey__description">
            Conte-nos sua experiência e nos ajude a melhorar.
          </Text>
        </div>

        <Button type="ghost" onClick={handleOpen} disabled={isWaiting}>
          Quero avaliar
        </Button>
      </div>
    </div>
  );
};

export default HotjarSurvey;
